// Revisado: 2025-12-18 [I18n Scan]
import React, { useState, useEffect } from 'react';
import authService from '../../../services/AuthService';
import { API_BASE_URL } from '../../../constants';

const SupportBonusEdit = ({ supportId, initialNormalEffects, initialUniqueEffects, onUpdate }) => {
    const [availableEffects, setAvailableEffects] = useState([]);
    const [selectedEffect, setSelectedEffect] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // Fetch Available Effects (Unique List from DB)
    useEffect(() => {
        const fetchEffects = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/support-effects`);
                if (res.ok) {
                    const data = await res.json();
                    setAvailableEffects(data);
                }
            } catch (err) {
                console.error("Failed to fetch effects list", err);
            }
        };
        fetchEffects();
    }, []);

    // Load Initial Data into Rows
    useEffect(() => {
        const newRows = [];

        // 1. Process Unique Effects
        if (initialUniqueEffects) {
            initialUniqueEffects.forEach(u => {
                newRows.push({
                    id: `u-${Date.now()}-${Math.random()}`,
                    dbId: u.Id, // Capture real DB ID for updates
                    effect: u.Effect,
                    type: 'Unique',
                    caps: [u.Bonus || 0, 0, 0, 0, 0], // Only idx 0 matters
                    isNew: false
                });
            });
        }

        // 2. Process Normal Effects
        // Data format: { Effect: "Speed Bonus", Bonuses: [0,0,1,1,2] }
        if (initialNormalEffects) {
            initialNormalEffects.forEach(n => {
                // Ensure array has 5 elements, fill with 0 if null
                const caps = (n.Bonuses || []).map(b => (b === null ? 0 : b));
                while (caps.length < 5) caps.push(0);

                newRows.push({
                    id: `n-${Date.now()}-${Math.random()}`,
                    effect: n.Effect,
                    type: 'Normal',
                    caps: caps,
                    isNew: false
                });
            });
        }

        setRows(newRows);
    }, [initialNormalEffects, initialUniqueEffects]);

    const addRow = () => {
        if (!selectedEffect) return;

        // Prevent duplicate Normal effects
        // User requested strict check: "Los normales no se deben repetir effectos con el mismo nombre."
        const isDuplicate = rows.some(row => row.effect === selectedEffect && row.type === 'Normal');

        if (isDuplicate) {
            alert(`The effect "${selectedEffect}" is already in the list as a Normal bonus. Duplicates are not allowed for Normal effects.`);
            return;
        }

        setRows([
            ...rows,
            {
                id: Date.now(),
                effect: selectedEffect,
                type: 'Normal', // Default
                caps: [0, 0, 0, 0, 0], // Default 0s
                isNew: true
            }
        ]);
        setSelectedEffect('');
    };

    const removeRow = async (rowIndex) => {
        const row = rows[rowIndex];

        // If it's an existing row, we might need to delete from DB immediately or wait for save?
        // User preference not strictly defined, but "Save" pattern implies bulk.
        // However, existing backend endpoints are granular per-effect.
        // Let's implement immediate delete for existing rows to avoid sync issues,
        // OR distinct save buttons per row (Matrix usually implies one big save?).
        // Backend has DELETE endpoint for normal and POST (update) logic.

        if (!row.isNew) {
            if (!window.confirm(`Delete existing bonus "${row.effect}"? This is immediate.`)) return;

            try {
                setLoading(true);
                let url;
                let method = 'DELETE';


                if (row.type === 'Normal') {
                    // DELETE /api/supports/<id>/bonus/normal?effect_name=...
                    url = `${API_BASE_URL}/api/supports/${supportId}/bonus/normal?effect_name=${encodeURIComponent(row.effect)}`;
                } else {
                    // For Unique, we usually just overwrite, but if we want to delete...
                    // Current backend only has POST (Update/Insert). 
                    // We might need to handle Unique deletion by sending empty/null?
                    // For now, removing from UI. Unique deletion might require backend tweak if strictly needed.
                    // But strictly speaking, we can just remove it from UI list and not save it? 
                    // Backend logic for Unique was "Delete existing... Insert new". 
                    // So actually Unique doesn't have a pure Delete endpoint yet.
                    alert("Deleting Unique bonuses specifically isn't fully supported by backend API yet. Use 'Save' on an empty row or contact dev.");
                    setLoading(false);
                    return;
                }

                if (url) {
                    const res = await fetch(url, {
                        method,
                        headers: {
                            'Authorization': `Bearer ${authService.getToken()}`
                        }
                    });
                    if (!res.ok) throw new Error('Delete failed');
                    if (onUpdate) onUpdate(); // Refresh parent
                }
            } catch (err) {
                alert(err.message);
                setLoading(false);
                return; // Don't remove from UI if failed
            } finally {
                setLoading(false);
            }
        }

        const updated = [...rows];
        updated.splice(rowIndex, 1);
        setRows(updated);
    };

    const handleCellChange = (rowIndex, colIndex, val) => {
        const updated = [...rows];
        const newValue = val === '' ? '' : Number(val);
        updated[rowIndex].caps[colIndex] = newValue;

        // Auto-fill logic (Ripple Effect)
        // Update subsequent caps if the new value is greater than or equal to current subsequent value
        if (newValue !== '') {
            for (let i = colIndex + 1; i < updated[rowIndex].caps.length; i++) {
                const currentNextVal = updated[rowIndex].caps[i] === '' ? 0 : updated[rowIndex].caps[i];

                // "El relleno debe ocurrir siempre y cuando el valor del cap siguiente sea menor o igual al cap agregado"
                // Only if updated value is >= existing subsequent value, we overwrite it.
                // Ensures we don't overwrite manually set higher peaks.
                if (currentNextVal <= newValue) {
                    updated[rowIndex].caps[i] = newValue;
                }
            }
        }

        setRows(updated);
    };

    const handleTypeChange = (rowIndex, type) => {
        const updated = [...rows];
        updated[rowIndex].type = type;
        setRows(updated);
    };

    const saveRow = async (row) => {
        try {
            setLoading(true);
            setMessage(null);
            let res;

            if (row.type === 'Normal') {
                // POST /api/supports/<id>/bonus/normal
                // Payload: { effect_name, bonuses: [v0, v1, v2, v3, v4] }
                const payload = {
                    effect_name: row.effect,
                    bonuses: row.caps.map(c => c === '' ? 0 : c)
                };

                res = await fetch(`${API_BASE_URL}/api/supports/${supportId}/bonus/normal`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error('Failed to save Normal bonus');

            } else {
                // Unique
                // POST /api/supports/<id>/bonus/unique
                const payload = {
                    effect: row.effect,
                    bonus: row.caps[0] === '' ? 0 : row.caps[0],
                    id: row.dbId // Pass ID if updating existing row
                };

                res = await fetch(`${API_BASE_URL}/api/supports/${supportId}/bonus/unique`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) throw new Error('Failed to save Unique bonus');
            }

            // Parse response to get updated data (e.g. ID for new unique bonuses)
            const data = await res.json();

            // Update local state instead of calling onUpdate() to avoid full page reload
            // User requested: "no seria mas facil si me enviara un mensaje diciendo 'se agrego' en vez de recargar la pagina completa?"
            const updatedRows = [...rows];
            const rowIndex = updatedRows.findIndex(r => r === row);
            if (rowIndex !== -1) {
                updatedRows[rowIndex] = {
                    ...updatedRows[rowIndex],
                    isNew: false,
                    // Assume backend returns the saved object or ID. 
                    // If Unique, we definitely need the ID for future updates.
                    dbId: data.id || data.Id || updatedRows[rowIndex].dbId
                };
                setRows(updatedRows);
            }

            setMessage(`Saved ${row.effect}!`);
            // if (onUpdate) onUpdate(); // REMOVED to prevent page reload/scroll reset

        } catch (err) {
            console.error(err);
            setMessage(`Error: ${err.message}`);
        } finally {
            setLoading(false);
            // Clear message after 3s
            setTimeout(() => setMessage(null), 3000);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="text-amber-500">✨</span> Bonus Effect Matrix
            </h3>

            {/* Controls */}
            <div className="flex gap-4 mb-6">
                <select
                    className="flex-grow p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    value={selectedEffect}
                    onChange={(e) => setSelectedEffect(e.target.value)}
                >
                    <option value="">-- Select Effect to Add --</option>
                    {availableEffects.map(ef => (
                        <option key={ef} value={ef}>{ef}</option>
                    ))}
                </select>
                <button
                    onClick={addRow}
                    disabled={!selectedEffect}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold shadow-sm transition-colors"
                >
                    + Add Effect
                </button>
            </div>

            {/* Matrix Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-700">
                    <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-xs">
                        <tr>
                            <th className="px-4 py-3 border-b">Effect Name</th>
                            <th className="px-4 py-3 border-b">Type</th>
                            {[0, 1, 2, 3, 4].map(i => (
                                <th key={i} className="px-2 py-3 border-b text-center w-20">Cap {i}<br /><span className="text-[10px] text-gray-400 font-normal">Level {30 + (i * 5)}</span></th>
                            ))}
                            <th className="px-4 py-3 border-b text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {rows.map((row, rIdx) => (
                            <tr key={row.id} className="hover:bg-blue-50/30 transition-colors">
                                <td className="px-4 py-3 font-medium text-gray-900">{row.effect}</td>
                                <td className="px-4 py-3">
                                    <select
                                        value={row.type}
                                        onChange={(e) => handleTypeChange(rIdx, e.target.value)}
                                        className="p-1 border border-gray-300 rounded text-xs"
                                    >
                                        <option value="Normal">Normal</option>
                                        <option value="Unique">Unique</option>
                                    </select>
                                </td>
                                {row.caps.map((val, cIdx) => {
                                    const isDisabled = row.type === 'Unique' && cIdx > 0;
                                    return (
                                        <td key={cIdx} className="px-2 py-3 text-center bg-gray-50/50">
                                            <input
                                                type="number"
                                                value={val}
                                                disabled={isDisabled}
                                                onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                                                className={`w-16 p-1 text-center border rounded focus:ring-2 focus:ring-blue-500 outline-none
                                                    ${isDisabled ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-300 font-mono text-blue-700 font-bold'}
                                                `}
                                                placeholder="0"
                                            />
                                        </td>
                                    );
                                })}
                                <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                                    <button
                                        onClick={() => saveRow(row)}
                                        disabled={loading}
                                        className="text-blue-600 hover:text-blue-800 text-xs font-bold uppercase tracking-wider px-2"
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => removeRow(rIdx)}
                                        className="text-red-500 hover:text-red-700"
                                        title="Remove Row"
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center py-8 text-gray-400 italic">
                                    No bonus effects assigned. Select one above to start.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Status Message */}
            {message && (
                <div className={`mt-4 p-3 rounded-lg text-sm font-medium text-center animate-pulse
                    ${message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}
                `}>
                    {message}
                </div>
            )}
        </div>
    );
};

export default SupportBonusEdit;
// Revisado: 2025-12-16
